import { useCallback, useEffect, useMemo, useState } from 'react';
import { SetURLSearchParams } from 'react-router-dom';
import { mutate as mutateGlobal } from 'swr';

import { FetcherParams } from '@/data/fetcher';
import { useGroupRunsStats } from '@/hooks/useSwr';
import { RuleSchema, SessionRuleMutateBody } from '@/types/schema';
import { utcTime } from '@/utils/utc-time';

import { useTabAwareFilters } from '../Rules/hooks/useTabAwareFilters';
import { SearchModel } from '../RunsTable/types';
import { HeaderTitleActionSlot, SplitViewPane } from '../SplitViewPane';
import { EvaluatorCrudForm } from './EvaluatorCrudForm';
import { SessionsEvaluatorCrudPaneHeader } from './EvaluatorCrudPaneHeader';
import { ThreadRuleForm } from './ThreadRuleForm';
import {
  SESSION_EVAL_CRUD_PANE_WIDTH,
  THREAD_AVAILABLE_PATHS,
} from './constants';
import { useThreadIdleSeconds } from './hooks/useThreadIdleSeconds';
import { useEvaluatorStore } from './store/evaluatorStore';
import { useCommitAndSaveRule } from './utils/useCommitAndSaveRule';

export type ThreadEvaluatorCrudPaneProps = {
  initialData?: RuleSchema;
  sessionId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSave: () => void;
  isSaving?: boolean;

  searchParams?: URLSearchParams;
  setSearchParams?: SetURLSearchParams;
};

function ThreadEvaluatorRuleCrudForm(
  props: ThreadEvaluatorCrudPaneProps & { backfill_from?: string | null }
) {
  const importEvaluator = useEvaluatorStore((state) => state.importEvaluator);
  const setAvailablePaths = useEvaluatorStore(
    (state) => state.setAvailablePaths
  );
  const setVariableMapping = useEvaluatorStore(
    (state) => state.setVariableMapping
  );
  const setName = useEvaluatorStore((state) => state.setName);
  const setSamplingRate = useEvaluatorStore((state) => state.setSamplingRate);

  const promptMessages = useEvaluatorStore((state) => state.promptMessages);
  const hubRef = useEvaluatorStore((state) => state.hubRef);

  // Determine when the main CRUD form can be rendered
  const isNewEvaluator = !props.initialData;
  const hasLoadedEvaluatorData = Boolean(
    props.initialData && (promptMessages || hubRef)
  );
  const initialEvaluator = props.initialData?.evaluators?.[0].structured;
  const shouldRenderCrudForm = isNewEvaluator || hasLoadedEvaluatorData;

  const { open, setOpen, onSave } = props;

  const [ruleForm, setRuleForm] = useState<{
    data: Partial<SessionRuleMutateBody>;
    isValid: boolean;
  }>({
    data: props.initialData ?? {},
    isValid: true,
  });

  const onFormChange = useCallback(
    (form: NonNullable<typeof ruleForm>) => {
      setRuleForm(form);
      if (form.data.display_name) {
        setName(form.data.display_name, true);
      }
      if (form.data.sampling_rate) {
        setSamplingRate(form.data.sampling_rate);
      }
    },
    [setName, setSamplingRate]
  );

  useEffect(() => {
    if (open) {
      importEvaluator(initialEvaluator, 'thread', props.backfill_from, 3);
      setName(props.initialData?.display_name ?? '', true);
      setSamplingRate(props.initialData?.sampling_rate ?? 1);

      // Note this is different from the other evaluators because
      // we set a static list of available paths for thread evaluators.
      setAvailablePaths(THREAD_AVAILABLE_PATHS);
      setVariableMapping({
        conversation: 'All messages',
      });
    }
  }, [
    open,
    initialEvaluator,
    props.backfill_from,
    importEvaluator,
    setName,
    props.initialData,
    setSamplingRate,
    setAvailablePaths,
    setVariableMapping,
  ]);

  const { searchParams, setSearchParams } = props;
  const threadFilter = searchParams?.get('threadsFilter') || '';
  const threadFilters = useMemo(
    () => ({ filter: threadFilter }),
    [threadFilter]
  );
  const setThreadFilters = useCallback(
    (filters: SearchModel) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (filters) {
        newSearchParams.set('threadsFilter', filters.filter ?? '');
      } else {
        newSearchParams.delete('threadsFilter');
      }
      setSearchParams?.(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const [filtersToUse, setFilters] = useTabAwareFilters({
    runTableFilters: threadFilters,
    setRunTableFilters: setThreadFilters,
    existingRuleFilters: props.initialData
      ? {
          filter: props.initialData?.filter,
          traceFilter: props.initialData?.trace_filter,
          treeFilter: props.initialData?.tree_filter,
        }
      : undefined,
    syncToTab: '1',
  });

  const { data: threadFilterStats } = useGroupRunsStats({
    session_id: props.sessionId,
    group_by: 'conversation',
    filter: filtersToUse.filter,
    start_time: utcTime().subtract(7, 'days').toISOString(),
  });

  const hubRefManifest = useEvaluatorStore((state) => state.hubRefManifest);

  const onSuccessSave = useCallback(async () => {
    await mutateGlobal((key: FetcherParams) => key?.url?.includes('commits'));
    setOpen(false);
    onSave();
  }, [setOpen, onSave]);

  const { saveRule, isSavingEvaluator } = useCommitAndSaveRule({
    sessionId: props.sessionId,
    existingRule: props.initialData,
    transformManifest: (manifest) => manifest,
    onSuccess: onSuccessSave,
    hubRefManifest,
    filter: filtersToUse.filter,
    traceFilter: filtersToUse.traceFilter,
    treeFilter: filtersToUse.treeFilter,
  });

  const { idleTime, setIdleTime, updateSessionIdleTime } = useThreadIdleSeconds(
    props.sessionId
  );

  return (
    <div className="overflow-y-auto p-6 pl-1">
      <HeaderTitleActionSlot.Fill>
        <SessionsEvaluatorCrudPaneHeader
          onClose={() => {
            setOpen(false);
          }}
          onSave={async (validatedEvaluator) => {
            await saveRule({ validatedEvaluator });
            await updateSessionIdleTime();
          }}
          isSaving={isSavingEvaluator}
          selectedEvaluator={props.initialData?.evaluators?.[0].structured}
          saveDisabled={!ruleForm?.isValid}
        />
      </HeaderTitleActionSlot.Fill>

      <div className="flex flex-col gap-4">
        <ThreadRuleForm
          initialData={props.initialData}
          sessionId={props.sessionId}
          onFormChange={onFormChange}
          filters={filtersToUse}
          setFilters={setFilters}
          threadFilterStats={threadFilterStats}
          onThreadIdleTimeChange={setIdleTime}
          threadIdleTime={idleTime}
        />

        {/* Render once evaluator data is available or when creating a new evaluator */}
        {shouldRenderCrudForm && (
          <EvaluatorCrudForm
            input={{}}
            showEvaluatorVersionWarning={false}
            isCreating={!props.initialData}
            hideFewShotConfig={true}
            evaluatedEntityType="thread-eval"
          />
        )}
      </div>
    </div>
  );
}

export function ThreadEvaluatorCrudPane(props: ThreadEvaluatorCrudPaneProps) {
  const { initialData, open, setOpen } = props;

  return (
    <SplitViewPane
      title={initialData ? 'Edit thread evaluator' : 'Create thread evaluator'}
      hideArrows
      open={open}
      onClose={() => setOpen(false)}
      defaultWidth={SESSION_EVAL_CRUD_PANE_WIDTH}
    >
      {open && <ThreadEvaluatorRuleCrudForm {...props} />}
    </SplitViewPane>
  );
}
